import { calculateFindingsLayout, type FindingWithLayoutInfo } from 'ts/commons/code/FindingsLayout';
import type { RenderableCode } from 'ts/commons/code/RenderedCode.context';
import { PathUtils } from 'ts/commons/PathUtils';
import type { FindingLikeType } from 'ts/perspectives/findings/FindingLikeType';
import { EExtendedResourceType, type EExtendedResourceTypeEntry } from 'typedefs/EExtendedResourceType';
import type { ElementLocationSubtype } from 'typedefs/ElementLocationSubtype';
import type { LineBasedRegion } from 'typedefs/LineBasedRegion';
import type { QualifiedNameLocation } from 'typedefs/QualifiedNameLocation';

/**
 * A finding with a isPending field that signals whether the finding is a pending review finding (not processed by the
 * backend yet).
 */
export type MaybePendingFindingLikeType = FindingLikeType & {
	isPending: boolean;
};

/** Result type for calculateLinesWithFindingIds. */
type LinesWithFindingsResult = {
	linesWithFindings: Map<number, FindingWithLayoutInfo[]>;
	maxColumnsOnAnyLine: number;
};

/**
 * Wrapper around a finding and a line-based region where the finding should be shown. The region is most of the time
 * the {@link TextRegionLocation}, but can also be a resolved {@link QualifiedNameLocation} or point to the secondary
 * location of a finding.
 */
export type FindingWithLineInfo = {
	finding: MaybePendingFindingLikeType;
	region: LineBasedRegion;
};

/** Builds a mapping from line numbers to the finding IDs that span those lines, and calculates layout information. */
export function calculateLinesWithFindingIds(
	findings: MaybePendingFindingLikeType[],
	tokenElement: RenderableCode,
	lineStartOffsets: number[],
	extendedResourceTypes: EExtendedResourceTypeEntry[],
	lineCount: number
): LinesWithFindingsResult {
	const findingsWithLineInfo = getFindingsWithLineInfo(
		tokenElement,
		lineStartOffsets,
		extendedResourceTypes,
		findings
	);
	const findingsWithLayoutInfo = calculateFindingsLayout(findingsWithLineInfo);

	let maxColumnsOnAnyLine = 0;
	for (const { column } of findingsWithLayoutInfo) {
		maxColumnsOnAnyLine = Math.max(maxColumnsOnAnyLine, column + 1);
	}

	return {
		linesWithFindings: groupByLine(lineCount, findingsWithLayoutInfo),
		maxColumnsOnAnyLine
	};
}

function getFindingsWithLineInfo(
	tokenElement: RenderableCode,
	lineStartOffsets: number[],
	extendedResourceTypes: EExtendedResourceTypeEntry[],
	findings: MaybePendingFindingLikeType[]
) {
	const getFindingRegion = (findingLocation: ElementLocationSubtype) => {
		return getStartAndEndLineNumberForFindingLocation(
			tokenElement.text,
			lineStartOffsets,
			findingLocation,
			extendedResourceTypes
		);
	};

	const findingsWithLineInfo: FindingWithLineInfo[] = [];

	for (const finding of findings) {
		const primaryFindingRegion = getFindingRegion(finding.location);
		if (primaryFindingRegion) {
			findingsWithLineInfo.push({ finding, region: primaryFindingRegion });
		}

		if (!('secondaryLocations' in finding && finding.secondaryLocations)) {
			continue;
		}
		for (const secondaryLocation of finding.secondaryLocations) {
			if (secondaryLocation.uniformPath !== finding.location.uniformPath) {
				// Skip secondary locations in other files
				continue;
			}
			const secondaryFindingRegion = getFindingRegion(secondaryLocation);
			if (secondaryFindingRegion) {
				findingsWithLineInfo.push({ finding, region: secondaryFindingRegion });
			}
		}
	}
	return findingsWithLineInfo;
}

function groupByLine(lineCount: number, findingsWithLayoutInfo: FindingWithLayoutInfo[]) {
	const linesWithFindingIds = new Map<number, FindingWithLayoutInfo[]>();
	for (let lineNumber = 1; lineNumber <= lineCount; lineNumber++) {
		linesWithFindingIds.set(lineNumber, []);
	}
	for (const findingWithLayoutInfo of findingsWithLayoutInfo) {
		for (
			let lineNumber = findingWithLayoutInfo.region.start;
			lineNumber <= findingWithLayoutInfo.region.end;
			lineNumber++
		) {
			// Cope with 'invalid' line information in findings, such as incorrect endLines
			linesWithFindingIds.get(lineNumber)?.push(findingWithLayoutInfo);
		}
	}
	return linesWithFindingIds;
}

/**
 * Adds the finding locations to the affected lines.
 *
 * @param location The finding's location
 */
function getStartAndEndLineNumberForFindingLocation(
	sourceCodeText: string,
	lineOffsets: number[],
	location: ElementLocationSubtype,
	resourceTypesForFile: EExtendedResourceTypeEntry[]
): LineBasedRegion | undefined {
	if (
		resourceTypesForFile.includes(EExtendedResourceType.SIMULINK_DATA_DICTIONARY.name) &&
		PathUtils.isQualifiedNameLocation(location)
	) {
		const oneBasedLineNumber = determineLineNumberForDataDictionaryFindingLocation(
			sourceCodeText,
			lineOffsets,
			location
		);
		return { start: oneBasedLineNumber, end: oneBasedLineNumber };
	} else if (PathUtils.isTextRegionLocation(location)) {
		const startLine = location.rawStartLine;
		let endLine = location.rawEndLine;
		if (!endLine) {
			endLine = startLine;
		}
		return { start: startLine, end: endLine };
	}
	return undefined;
}

/**
 * Returns the line number in which a given finding location in a Simulink Data Dictionary file should be displayed.
 *
 * Findings in Simulink Data Dictionaries have QualifiedNameLocations. If the finding is on the entry with name
 * "myEntry" in data dictionary "foo.sldd", then the qualifiedName is "myEntry".
 *
 * To determine the line number, we assume that lines start with entry names followed by a ": ". This is the way
 * SimulinkDataDictionary.buildLineBasedRepresentation encodes its contents.
 */
function determineLineNumberForDataDictionaryFindingLocation(
	sourceCodeText: string,
	lineOffsets: number[],
	location: QualifiedNameLocation
) {
	const elementName = location.qualifiedName;
	const searchString = elementName + ': ';
	let oneBasedLineNumber = 0;
	for (let lineNumber = 0; lineNumber < lineOffsets.length; lineNumber++) {
		const lineOffset = lineOffsets[lineNumber]!;
		if (sourceCodeText.startsWith(searchString, lineOffset)) {
			// Line numbers must be given 1-based (first line has number 1)
			oneBasedLineNumber = lineNumber + 1;
			break;
		}
	}
	return oneBasedLineNumber;
}
